import {
  setClarityTags,
  trackClarityEvent,
  upgradeClaritySession
} from "./clarity";
import {
  extractBusinessCodes,
  inferClarityFlow,
  parseGraphQLOperationKind,
  parseGraphQLOperationName,
  sanitizeClarityToken
} from "./clarityHelpers";

export type ClarityApiOutcome =
  | "success"
  | "business_failure"
  | "graphql_error"
  | "network_error"
  | "unauthorized";

export type TrackClarityApiParams = {
  query: string;
  outcome: ClarityApiOutcome;
  /** Optional GraphQL/banner/business code */
  code?: string;
  kindOverride?: "query" | "mutation" | "subscription" | "upload" | "rest";
};

/**
 * Track a GraphQL/REST API outcome for Clarity Smart Events + filter tags.
 * Covers product/category/order/education/membership/auth flows via operation names.
 */
export const trackClarityApiCall = ({
  query,
  outcome,
  code,
  kindOverride
}: TrackClarityApiParams): void => {
  const operation = parseGraphQLOperationName(query);
  const kind = kindOverride ?? parseGraphQLOperationKind(query);
  const flow = inferClarityFlow(operation);
  const safeOp = sanitizeClarityToken(operation);
  const safeOutcome = sanitizeClarityToken(outcome);
  const safeCode = code ? sanitizeClarityToken(code, 48) : undefined;

  setClarityTags({
    api_operation: safeOp,
    api_outcome: safeOutcome,
    api_kind: kind,
    api_flow: flow,
    ...(safeCode ? { api_code: safeCode } : {})
  });

  const eventName = safeCode
    ? `api:${safeOutcome}:${safeOp}:${safeCode}`
    : `api:${safeOutcome}:${safeOp}`;
  trackClarityEvent(eventName.slice(0, 100));

  if (outcome === "network_error" || outcome === "unauthorized") {
    upgradeClaritySession(`api_${outcome}`);
  }
};

/** Wrap a GraphQL-style call and emit Clarity events for success/failure. */
export const withClarityApiTracking = async <T>(
  query: string,
  run: () => Promise<T>,
  options?: { kindOverride?: TrackClarityApiParams["kindOverride"] }
): Promise<T> => {
  try {
    const data = await run();
    const codes = extractBusinessCodes(data);
    const nonOk = codes.find((c) => c !== "OK" && c !== "ok");
    if (nonOk) {
      trackClarityApiCall({
        query,
        outcome: "business_failure",
        code: nonOk,
        kindOverride: options?.kindOverride
      });
    } else {
      trackClarityApiCall({
        query,
        outcome: "success",
        code: codes[0],
        kindOverride: options?.kindOverride
      });
    }
    return data;
  } catch (error) {
    const message =
      error instanceof Error ? error.message : String(error ?? "error");
    if (message.includes("UNAUTHORIZED") || message === "UNAUTHORIZED") {
      trackClarityApiCall({
        query,
        outcome: "unauthorized",
        code: "UNAUTHORIZED",
        kindOverride: options?.kindOverride
      });
    } else if (
      message.includes("Failed to fetch") ||
      message.includes("NetworkError") ||
      message.includes("Load failed")
    ) {
      trackClarityApiCall({
        query,
        outcome: "network_error",
        kindOverride: options?.kindOverride
      });
    } else {
      trackClarityApiCall({
        query,
        outcome: "graphql_error",
        code: sanitizeClarityToken(message, 40),
        kindOverride: options?.kindOverride
      });
    }
    throw error;
  }
};

export const trackClarityRestCall = (
  operationName: string,
  outcome: ClarityApiOutcome,
  code?: string
): void => {
  trackClarityApiCall({
    query: `query ${operationName} { __typename }`,
    outcome,
    code,
    kindOverride: "rest"
  });
};

export const trackClarityUiClick = (
  label: string,
  extras?: {
    testId?: string;
    href?: string;
  }
): void => {
  const safeLabel = sanitizeClarityToken(label, 80);
  setClarityTags({
    ui_click: safeLabel,
    ...(extras?.testId
      ? { ui_click_testid: sanitizeClarityToken(extras.testId, 80) }
      : {}),
    ...(extras?.href
      ? { ui_click_href: sanitizeClarityToken(extras.href, 80) }
      : {})
  });
  trackClarityEvent(`click:${safeLabel}`.slice(0, 100));
};
